import { useEffect, useState } from "react"

// Three-dot "thinking…" placeholder shown while we wait for the
// agent's reply to arrive. Visual structure mirrors ChatMessageBubble
// so the layout doesn't jump when the real bubble lands.
type Props = {
  label?: string
  /** Side the indicator sits on. Defaults to left ("they're typing back"). */
  side?: "left" | "right"
}

// Escalation tiers — what the subtitle says as wait time stretches.
// Tuned so the operator sees movement before they reach for the refresh
// button: a fresh "thinking" dot for 30s feels worse than the same dot
// with a self-aware caption escalating in difficulty. Every 10s the
// label progressively admits the problem is harder.
//
// Caps out at 90s — that's the Anthropic SDK's per-request timeout
// (REQUEST_TIMEOUT_SECONDS in agents/ai_model_providers/anthropic.rb),
// after which our own client raises and the indicator unmounts.
//
// Tiers are inclusive of the lower bound (e.g. >= 30s shows tier 3).
const TIERS: { atSeconds: number; subtitle: string }[] = [
  { atSeconds:  0, subtitle: "thinking" },
  { atSeconds: 10, subtitle: "still thinking…" },
  { atSeconds: 20, subtitle: "hard problem" },
  { atSeconds: 30, subtitle: "harder problem" },
  { atSeconds: 40, subtitle: "real puzzler" },
  { atSeconds: 50, subtitle: "this one's a beast" },
  { atSeconds: 60, subtitle: "heavy lifting" },
  { atSeconds: 70, subtitle: "cracking it open" },
  { atSeconds: 80, subtitle: "almost have it" },
  { atSeconds: 90, subtitle: "any second now…" },
]

export function ChatThinkingIndicator({ label = "Agent", side: _side }: Props) {
  void _side

  // Tick once per second so the subtitle escalates without re-rendering
  // every frame. Reset when the indicator unmounts (each new turn
  // remounts, so elapsed restarts at 0 — exactly what we want).
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const subtitle = TIERS.reduce((acc, t) => elapsed >= t.atSeconds ? t.subtitle : acc, TIERS[0].subtitle)

  const Avatar = (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-vip-gold/40 bg-vip-goldMute text-vip-goldSoft">
      <span className="text-xs">…</span>
    </div>
  )
  return (
    <div className="flex w-full gap-3">
      {Avatar}
      <div className="flex max-w-[640px] min-w-0 flex-col gap-1.5">
        <div className="flex items-baseline gap-2 text-[11px]">
          <span className="font-semibold text-app-text">{label}</span>
          <span className="text-app-subtle">{subtitle}</span>
        </div>
        <div className="rounded-card border border-border bg-app-panel2 px-4 py-2.5">
          <div className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:0ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:200ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:400ms]" />
          </div>
        </div>
      </div>
    </div>
  )
}
